import { ChangePasswordDialog } from "@/components/change-password-dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/hooks/use-toast";
import { useUpdateInternalProfile } from "@/hooks/useInternalUsers";
import { useAuth } from "@/lib/auth";
import { getPrimaryRole, roleDisplayNames } from "@/lib/permissions";
import { Calendar, Camera, Key, Mail, Save, Shield, User } from "lucide-react";
import { useEffect, useState } from "react";
import { z } from "zod";

export default function Profile() {
  const { user, refreshUser } = useAuth();
  const { toast } = useToast();
  const [isEditing, setIsEditing] = useState(false);
  const [displayName, setDisplayName] = useState(user?.name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [isSaving, setIsSaving] = useState(false);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [removeAvatarFlag, setRemoveAvatarFlag] = useState(false);
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);

  useEffect(() => {
    return () => {
      if (avatarPreview) {
        URL.revokeObjectURL(avatarPreview);
      }
    };
  }, [avatarPreview]);

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (avatarPreview) URL.revokeObjectURL(avatarPreview);
    setAvatarFile(file);
    setAvatarPreview(URL.createObjectURL(file));
    setRemoveAvatarFlag(false);
    e.target.value = "";
  };

  const handleRemoveAvatar = () => {
    if (avatarPreview) URL.revokeObjectURL(avatarPreview);
    setAvatarFile(null);
    setAvatarPreview(null);
    setRemoveAvatarFlag(true);
  };

  const resetAvatarState = () => {
    if (avatarPreview) URL.revokeObjectURL(avatarPreview);
    setAvatarFile(null);
    setAvatarPreview(null);
    setRemoveAvatarFlag(false);
  };

  const updateProfileMutation = useUpdateInternalProfile();

  const getInitials = (name?: string) => {
    if (!name) return "U";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const getRoleBadgeVariant = (role: string) => {
    switch (role) {
      case "super_admin":
        return "default" as const;
      case "admin":
        return "secondary" as const;
      case "coach_manager":
        return "outline" as const;
      default:
        return "outline" as const;
    }
  };

  // EVO-90: Save stays disabled until something really differs (the server
  // lowercases email, so compare the same way).
  const hasChanges =
    !!user &&
    (displayName.trim() !== (user.name || "") ||
      email.trim().toLowerCase() !== (user.email || "").toLowerCase() ||
      !!avatarFile ||
      (removeAvatarFlag && !!user.avatarUrl));

  const handleSave = async () => {
    if (!hasChanges) return;
    if (!displayName.trim()) {
      toast({
        title: "Error",
        description: "Display name cannot be empty.",
        variant: "destructive",
      });
      return;
    }
    // Same rule as Settings, so both screens accept the same emails.
    if (!z.string().email().safeParse(email.trim()).success) {
      toast({
        title: "Error",
        description: "Invalid email",
        variant: "destructive",
      });
      return;
    }

    setIsSaving(true);
    try {
      await updateProfileMutation.mutateAsync({
        name: displayName.trim(),
        email: email.trim(),
        avatar: avatarFile ?? undefined,
        removeAvatar: removeAvatarFlag,
      });

      await refreshUser();

      resetAvatarState();
      setIsEditing(false);
      toast({
        title: "Profile updated",
        description: "Your profile has been saved successfully.",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error?.message || "Failed to update profile.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (!user) {
    return (
      <div className="p-6">
        <p className="text-muted-foreground">Loading profile...</p>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 max-w-2xl mx-auto space-y-6">
      <div>
        <h1
          className="text-2xl lg:text-3xl font-serif font-semibold tracking-tight"
          data-testid="text-profile-title"
        >
          My Profile
        </h1>
        <p className="text-muted-foreground mt-1">
          Manage your account settings
        </p>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-4">
            <div className="flex flex-col items-center gap-2">
              {isEditing ? (
                <label
                  className="relative group cursor-pointer"
                  data-testid="label-avatar-upload"
                >
                  <Avatar className="h-16 w-16 ring-2 ring-primary/20">
                    {(avatarPreview ||
                      (!removeAvatarFlag && user.avatarUrl)) && (
                      <AvatarImage
                        src={avatarPreview || user.avatarUrl || undefined}
                        alt={user.name || user.email}
                        className="object-cover"
                      />
                    )}
                    <AvatarFallback className="bg-primary/20 text-primary text-xl font-medium">
                      {getInitials(
                        user.name || user.userProfile?.username || user.email,
                      )}
                    </AvatarFallback>
                  </Avatar>
                  <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                    <Camera className="h-5 w-5 text-white" />
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleAvatarChange}
                    className="hidden"
                    data-testid="input-avatar-upload"
                  />
                </label>
              ) : (
                <Avatar className="h-16 w-16 ring-2 ring-primary/20">
                  {user.avatarUrl && (
                    <AvatarImage
                      src={user.avatarUrl}
                      alt={user.name || user.email}
                      className="object-cover"
                    />
                  )}
                  <AvatarFallback className="bg-primary/20 text-primary text-xl font-medium">
                    {getInitials(
                      user.name || user.userProfile?.username || user.email,
                    )}
                  </AvatarFallback>
                </Avatar>
              )}
              {isEditing &&
                (avatarFile || (user.avatarUrl && !removeAvatarFlag)) && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-auto px-2 py-1 text-xs text-muted-foreground"
                    onClick={handleRemoveAvatar}
                    data-testid="button-remove-avatar"
                  >
                    Remove avatar
                  </Button>
                )}
            </div>
            <div className="flex-1">
              <CardTitle data-testid="text-profile-name">
                {user.name || user.userProfile?.username || user.email}
              </CardTitle>
              <CardDescription className="flex items-center gap-2 mt-1">
                <Badge
                  variant={getRoleBadgeVariant(
                    getPrimaryRole(user.roles) ?? "admin",
                  )}
                >
                  {user.roles?.map((r) => roleDisplayNames[r]).join(", ") ??
                    "—"}
                </Badge>
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="displayName" className="flex items-center gap-2">
                <User className="h-4 w-4 text-muted-foreground" />
                Display Name
              </Label>
              {isEditing ? (
                <Input
                  id="displayName"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  data-testid="input-display-name"
                />
              ) : (
                <p className="text-sm py-2" data-testid="text-display-name">
                  {user.name || user.userProfile?.username || user.email}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-muted-foreground" />
                Email Address
              </Label>
              {isEditing ? (
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="input-profile-email"
                />
              ) : (
                <p className="text-sm py-2" data-testid="text-email">
                  {user.email}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Shield className="h-4 w-4 text-muted-foreground" />
                Role
              </Label>
              <p className="text-sm py-2" data-testid="text-role">
                {user.roles?.map((r) => roleDisplayNames[r]).join(", ") ?? "—"}
              </p>
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                Member Since
              </Label>
              <p className="text-sm py-2 text-muted-foreground">
                {user.createdAt
                  ? new Date(user.createdAt).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    })
                  : "Unknown"}
              </p>
            </div>
          </div>

          <Separator />

          <div className="flex items-center justify-between gap-4 flex-wrap">
            {isEditing ? (
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsEditing(false);
                    setDisplayName(user.name || "");
                    setEmail(user.email || "");
                    resetAvatarState();
                  }}
                  data-testid="button-cancel-edit"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={isSaving || !hasChanges}
                  data-testid="button-save-profile"
                >
                  <Save className="h-4 w-4 mr-2" />
                  {isSaving ? "Saving..." : "Save Changes"}
                </Button>
              </div>
            ) : (
              <Button
                variant="outline"
                onClick={() => {
                  setDisplayName(user.name || "");
                  setEmail(user.email || "");
                  setIsEditing(true);
                }}
                data-testid="button-edit-profile"
              >
                Edit Profile
              </Button>
            )}
            <Button
              variant="ghost"
              onClick={() => setIsChangePasswordOpen(true)}
              data-testid="button-change-password"
            >
              <Key className="h-4 w-4 mr-2" />
              Change Password
            </Button>
          </div>
        </CardContent>
      </Card>

      <ChangePasswordDialog
        open={isChangePasswordOpen}
        onOpenChange={setIsChangePasswordOpen}
      />
    </div>
  );
}
